import { StepType } from '@/types/automationFields.ts';
import {
  ASYNC_SUB_AUTOMATION_OUTPUT_ID,
  AutomationStep,
  RepeatStep,
  SubAutomationExecutionMode,
  SubAutomationStep,
  TransformDataStep,
} from '@/types/automationSteps.ts';
import { AutomationVersion, PinnedOutput } from '@/types/automations.ts';

type PinnedOutputsVersionLike = Pick<
  AutomationVersion,
  'steps' | 'pinned_outputs' | 'pinned_steps'
>;
export const REPEAT_LATEST_OUTPUT_SUFFIX = '-latest';
export const REPEAT_ALL_OUTPUT_SUFFIX = '-all';

export const normalizePinnedOutputId = (outputId?: string | null): string => outputId ?? '';

export const getStepPinnedOutputs = (step: AutomationStep): PinnedOutput[] => {
  const outputIds = getStepOutputIds(step);
  return dedupePinnedOutputs(
    outputIds.map((outputId) => ({
      step_id: step.step_id,
      output_id: normalizePinnedOutputId(outputId),
    })),
  );
};

export const getEffectivePinnedOutputs = (
  version?: PinnedOutputsVersionLike | null,
): PinnedOutput[] => {
  if (!version) return [];

  if (version.pinned_outputs.length > 0) {
    return dedupePinnedOutputs(version.pinned_outputs);
  }

  const pinnedStepIds = new Set(version.pinned_steps);
  if (pinnedStepIds.size === 0) return [];

  return dedupePinnedOutputs(
    version.steps.flatMap((step) =>
      pinnedStepIds.has(step.step_id) ? getStepPinnedOutputs(step) : [],
    ),
  );
};

export const isStepPinned = (
  version: PinnedOutputsVersionLike | null | undefined,
  step: AutomationStep | null | undefined,
): boolean => {
  if (!version || !step) return false;

  const stepPinnedOutputs = getStepPinnedOutputs(step);
  if (stepPinnedOutputs.length === 0) return false;

  const pinnedOutputKeys = new Set(getEffectivePinnedOutputs(version).map(toPinnedOutputKey));
  return stepPinnedOutputs.every((output) => pinnedOutputKeys.has(toPinnedOutputKey(output)));
};

export const isOutputPinned = (
  version: PinnedOutputsVersionLike | null | undefined,
  stepId: string | null | undefined,
  outputId?: string | null,
): boolean => {
  if (!version || !stepId) return false;

  const pinnedOutputKeys = new Set(getEffectivePinnedOutputs(version).map(toPinnedOutputKey));
  return pinnedOutputKeys.has(
    toPinnedOutputKey({
      step_id: stepId,
      output_id: normalizePinnedOutputId(outputId),
    }),
  );
};

export const toggleStepPinnedOutputs = (
  version: PinnedOutputsVersionLike,
  step: AutomationStep,
): PinnedOutput[] => {
  const currentPinnedOutputs = getEffectivePinnedOutputs(version);
  const stepPinnedOutputs = getStepPinnedOutputs(step);

  if (stepPinnedOutputs.length === 0) {
    return currentPinnedOutputs;
  }

  const stepPinnedOutputKeys = new Set(stepPinnedOutputs.map(toPinnedOutputKey));
  const remainingPinnedOutputs = currentPinnedOutputs.filter(
    (output) => !stepPinnedOutputKeys.has(toPinnedOutputKey(output)),
  );

  if (isStepPinned(version, step)) {
    return remainingPinnedOutputs;
  }

  return dedupePinnedOutputs([...remainingPinnedOutputs, ...stepPinnedOutputs]);
};

export const togglePinnedOutput = (
  version: PinnedOutputsVersionLike,
  stepId: string,
  outputId?: string | null,
): PinnedOutput[] => {
  const currentPinnedOutputs = getEffectivePinnedOutputs(version);
  const normalizedOutputId = normalizePinnedOutputId(outputId);
  const targetOutput = { step_id: stepId, output_id: normalizedOutputId };
  const targetKey = toPinnedOutputKey(targetOutput);

  if (isOutputPinned(version, stepId, normalizedOutputId)) {
    return currentPinnedOutputs.filter((output) => toPinnedOutputKey(output) !== targetKey);
  }

  return dedupePinnedOutputs([...currentPinnedOutputs, targetOutput]);
};

const getStepOutputIds = (step: AutomationStep): string[] => {
  switch (step.step_type) {
    case StepType.Trigger:
      return [];
    case StepType.TransformData:
      return getTransformOutputIds(step);
    case StepType.Iterate:
      return step.data_outputs.map((output) => output.output_id);
    case StepType.Repeat:
      return getRepeatOutputIds(step);
    case StepType.Retry:
      return step.data_outputs.map((output) => output.output_id);
    case StepType.FailureHandler:
      return [
        ...step.protected_success_outputs.map((output) => output.output_id),
        ...(step.recovery_receipt_output ? [step.recovery_receipt_output.output_id] : []),
        ...(step.recovery_continuation_outputs ?? []).map((output) => output.output_id),
      ];
    case StepType.Conditional:
      return step.data_outputs.map((output) => output.output_id);
    case StepType.SubAutomation:
      return getSubAutomationOutputIds(step);
    case StepType.LocalSubflowCall:
      return step.output_bindings.map((output) => output.output_id);
    case StepType.Dispatch:
    case StepType.ExtractData:
    case StepType.RunMetadata:
    case StepType.CreateDirectory:
    case StepType.GetFileFromDirectory:
    case StepType.Filter:
    case StepType.Validate:
    case StepType.HumanDecisionGate:
      return [''];
    default:
      return [];
  }
};

const getTransformOutputIds = (step: TransformDataStep): string[] =>
  step.data_outputs && step.data_outputs.length > 0
    ? step.data_outputs.map((output) => output.output_id)
    : [''];

const getRepeatOutputIds = (step: RepeatStep): string[] =>
  step.repeat_data_inputs.flatMap((input) => [
    `${input.output_id}${REPEAT_LATEST_OUTPUT_SUFFIX}`,
    `${input.output_id}${REPEAT_ALL_OUTPUT_SUFFIX}`,
  ]);

const getSubAutomationOutputIds = (step: SubAutomationStep): string[] =>
  step.execution_mode === SubAutomationExecutionMode.Async
    ? [ASYNC_SUB_AUTOMATION_OUTPUT_ID]
    : step.binding_mode === 'composition'
      ? step.expected_interface.outputs.map((port) => port.port_id)
      : step.target_pinned_outputs.map((output) => output.exposed_output_id);

const dedupePinnedOutputs = (pinnedOutputs: PinnedOutput[]): PinnedOutput[] => {
  const seen = new Set<string>();
  return pinnedOutputs.filter((output) => {
    const key = toPinnedOutputKey(output);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

const toPinnedOutputKey = (output: PinnedOutput): string =>
  `${output.step_id}:${normalizePinnedOutputId(output.output_id)}`;
